import {
  type AutocompleteChangeDetails,
  type AutocompleteChangeReason,
  Box,
  Button,
  type ButtonPropsColorOverrides,
  Popover,
  Stack,
} from '@mui/material'
import {
  type FormEvent,
  type SyntheticEvent,
  useEffect,
  useRef,
  useState,
} from 'react'
import {
  GaForm,
  type GaFormHandle,
  type GaFormProps,
} from '@/features/analytics/gaForm/GaForm'
import { useDeferredGaSubmit } from '@/features/analytics/gaForm/useDeferredGaSubmit'
import {
  useAddMediaToCollectionMutation,
  useCreateCollectionMutation,
} from '@/features/collections/api'
import { CollectionSelect } from '@/features/collections/components/CollectionSelect'
import type { Collection } from '@/features/collections/types'
import { useSearch } from '@/features/search/hooks/useSearch'
import { useAppSelector } from '@/redux/hooks'

type Value = Collection | null

const baseGaFormProps: Omit<GaFormProps, 'children'> = {
  formName: 'add to collection',
  formId: 'add-to-collection',
  formLocation: 'main',
  formDestination: 'emc/collection',
  formSubmitText: 'Create New',
}

export const AddToCollection = () => {
  const gaFormRef = useRef<GaFormHandle>(null)
  const [open, setOpen] = useState<boolean>(false)
  const [value, setValue] = useState<Value>(null)
  const [buttonColor, setButtonColor] = useState<
    ButtonPropsColorOverrides | undefined
  >(undefined)
  const latestContentRef = useRef<string | null>(null)

  const getIsNew = (value: Value): boolean => !value?.collectionCode
  const isNew = getIsNew(value)

  const [buttonText, setButtonText] = useState<string>(
    isNew ? 'Create New' : 'Add Media',
  )

  const ref = useRef(null)
  const { selectors } = useSearch()
  const selection = useAppSelector(selectors.selectCurrentSelectionRows)
  const disabled = selection.length === 0

  const [addMedia, addMediaRequest] = useAddMediaToCollectionMutation()
  const [createCollection, createCollectionRequest] =
    useCreateCollectionMutation()

  const requests = [addMediaRequest, createCollectionRequest]
  const isSuccess = requests.some((r) => r.isSuccess)
  const isError = requests.some((r) => r.isError)
  const isLoading = requests.some((r) => r.isLoading)
  const { markPending } = useDeferredGaSubmit(gaFormRef, {
    isFetching: isLoading,
    isSuccess: isSuccess,
    isError: isError,
  })

  const handleOpen = () => setOpen(true)
  const handleClose = () => {
    if (!isError) {
      handleReset()
    }
    setOpen(false)
  }

  const handleReset = () => {
    requests.forEach((r) => { r.reset() })
    setOpen(false)
    setValue(null)
    setButtonText(getIsNew(value) ? 'Create New' : 'Add Media')
    setButtonColor('initial')
  }

  const handleChange = (
    _event: SyntheticEvent<Element, Event>,
    value: Value | null,
    _reason: AutocompleteChangeReason,
    _details: AutocompleteChangeDetails,
  ) => {
    setButtonText(getIsNew(value) ? 'Create New' : 'Add Media')
    setButtonColor(undefined)
    setValue(value)
  }

  const handleSubmit = (_event?: FormEvent<HTMLFormElement>) => {
    if (!value || isLoading) return

    if (isSuccess) {
      handleClose()
      return
    }

    const mediaCodes = selection.map((select) => select.mediaCode)

    if (isNew) {
      const title = value.collectionName
      latestContentRef.current = `Create Collection: ${title} | Media Codes: ${mediaCodes.join(', ')}`
      markPending()
      createCollection({ mediaCodes, title })
    } else {
      const { collectionCode } = value
      latestContentRef.current = `Add Media: ${collectionCode} | Media Codes: ${mediaCodes.join(', ')}`
      markPending()
      addMedia({ collectionCode, mediaCodes })
    }
  }

  useEffect(() => {
    if (isSuccess) {
      setButtonColor('success')
      if (createCollectionRequest.isSuccess) {
        setButtonText('Created New Collection')
      }
      if (addMediaRequest.isSuccess) {
        setButtonText('Added Media to Collection')
      }
    }
    if (isError) {
      setButtonColor('error')
      setButtonText('Error with updating collection')
    }
  }, [isSuccess, isError, createCollectionRequest.isSuccess, addMediaRequest.isSuccess])

  const gaFormProps: Omit<GaFormProps, 'children'> = {
    ...baseGaFormProps,
    getFormContent: () => latestContentRef.current,
    formStatus: isError ? 'error' : isSuccess ? 'success' : null,
    deferGaSubmit: true,
    onSubmit: handleSubmit,
  }

  return (
    <>
      <Button ref={ref} disabled={disabled} onClick={handleOpen}>
        Add to Collection
      </Button>
      <Popover
        open={open}
        onClose={handleClose}
        anchorEl={ref.current}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
      >
        <GaForm ref={gaFormRef} {...gaFormProps}>
          <Box width={600} p={2}>
            <Box flex={1}>
              <CollectionSelect onChange={handleChange} value={value} />
            </Box>
            <Stack direction={'row'} py={1}>
              <Box marginLeft="auto" />
              <Button
                loading={isLoading}
                disabled={!value}
                type="submit"
                color={buttonColor}
              >
                {buttonText}
              </Button>
            </Stack>
          </Box>
        </GaForm>
      </Popover>
    </>
  )
}
//TODO Fix these typescript mismatches, autoselect type is slightly different from the one here.
